import React, { useEffect, useRef } from 'react';
import { Box, Typography, Divider, Grid } from '@mui/material';
import html2pdf from 'html2pdf.js';
import { format } from 'date-fns';
import { ITransaction } from 'common/transactions.interface';
import { formatNumberToBRL, formatTaxNumber } from 'utils/formatters';
import { useSnackbar } from 'notistack';
import LogoCelcoin from 'assets/images/logo_celcoin_branco.png';

type Props = {
  open: boolean;
  transaction?: ITransaction;
  onClose?: () => void;
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box mt={3}>
    <Typography fontSize={14} fontWeight={700} color="#5A5959" letterSpacing={0.5}>
      {title}
    </Typography>
    <Divider sx={{ my: 1.5 }} />
    {children}
  </Box>
);

const Info = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography fontSize={14} fontWeight={700} color="text.secondary">
      {label}
    </Typography>
    <Typography fontSize={14}>{!value || value === 'null' ? '-' : value}</Typography>
  </Box>
);

const ProofOfPaymentPixDownload = ({ open, transaction, onClose }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const generatingRef = useRef(false);
  const { enqueueSnackbar } = useSnackbar();

  const formattedDate = transaction ? format(new Date(transaction.createdAt), "dd/MM/yyyy 'às' HH:mm:ss") : '';

  const generatePdf = async () => {
    if (!contentRef.current) return null;

    const opt = {
      filename: 'comprovante-pix.pdf',
      image: { type: 'jpeg' as const, quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
    };

    return html2pdf().set(opt).from(contentRef.current).outputPdf('blob');
  };

  useEffect(() => {
    const run = async () => {
      if (!open) return;
      if (!transaction) return;
      if (generatingRef.current) return;

      generatingRef.current = true;

      const { fonts } = document as any;

      if (fonts?.ready) {
        await fonts.ready;
      }

      // eslint-disable-next-line no-promise-executor-return
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const blob = await generatePdf();

      if (blob) {
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = 'comprovante-pix.pdf';
        link.click();

        URL.revokeObjectURL(url);

        enqueueSnackbar('Comprovante PIX gerado com sucesso!', { variant: 'success' });
      }

      generatingRef.current = false;

      onClose?.();
    };

    run();
  }, [open, transaction]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: '-10000px',
        backgroundColor: '#fff',
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          width: '794px',
          height: '1122px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '&, & *': {
            fontFamily: '"Segoe UI", Roboto, Helvetica, Arial, sans-serif !important',
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#806DFB',
            px: '40px',
            py: '30px',
            color: '#EDE9FF',
          }}
        >
          <Box
            component="img"
            src={LogoCelcoin}
            alt="Logo"
            sx={{
              height: 50,
              display: 'block',
            }}
          />
        </Box>

        {/* Content */}
        <Box px={6}>
          <Box textAlign="center" mb={4}>
            <Typography fontSize={24} fontWeight={600}>
              Comprovante de pagamento PIX
            </Typography>

            <Typography fontSize={14} color="text.secondary">
              Enviado no dia {formattedDate}
            </Typography>
          </Box>

          <Box mb={4} textAlign="center">
            <Typography fontSize={14} color="text.secondary">
              Valor
            </Typography>

            <Typography fontSize={24} fontWeight={700} color="#0D47A1">
              {formatNumberToBRL(transaction?.value)}
            </Typography>
          </Box>

          <Section title="PAGADOR">
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Info label="Nome" value={transaction?.account?.businessName} />
              </Grid>

              <Grid item xs={6}>
                <Info label="CNPJ" value={formatTaxNumber(transaction?.account?.cnpj)} />
              </Grid>
            </Grid>
          </Section>

          <Section title="FAVORECIDO">
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Info label="Destinatário" value={transaction?.counterpartyName} />
              </Grid>

              <Grid item xs={6}>
                <Info label="CPF/CNPJ" value={formatTaxNumber(transaction?.counterpartyTaxNumber)} />
              </Grid>

              <Grid item xs={4}>
                <Info label="Banco" value={transaction?.counterpartyBankCode} />
              </Grid>

              <Grid item xs={4}>
                <Info label="Agência" value={transaction?.counterpartyBankBranch} />
              </Grid>

              <Grid item xs={4}>
                <Info label="Conta" value={transaction?.counterpartyAccount} />
              </Grid>
            </Grid>
          </Section>

          <Box mt={4}>
            <Info label="Identificador do pagamento" value={transaction?.endToEndId} />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#806DFB',
            px: '40px',
            py: '30px',
            color: '#EDE9FF',
          }}
        >
          <Typography fontSize={16}>
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Torqi
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProofOfPaymentPixDownload;
